import { useEffect, useState } from 'react';
import { Box, Group, Loader, Select, Stack, Table, Text, Title } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import HttpClient from '../helpers/HttpClient';

interface PeriodStats {
  matches: number;
  wins: number;
  draws: number;
  losses: number;
  points: number;
  goalsScored: number;
  goalsConceded: number;
  topPlayers: { name: string; minutes: number }[];
}

interface MatchStatsResponse {
  current: PeriodStats;
  previous: PeriodStats;
}

interface FilterOptions {
  teams: string[];
  seasons: string[];
  competitions: string[];
}

const empty: PeriodStats = { matches: 0, wins: 0, draws: 0, losses: 0, points: 0, goalsScored: 0, goalsConceded: 0, topPlayers: [] };

function defaultFrom() {
  const d = new Date();
  d.setMonth(d.getMonth() - 6);
  d.setHours(0, 0, 0, 0);
  return d;
}

type DiffSentiment = 'positive' | 'negative' | 'neutral';

function DiffCell({ value, sentiment }: { value: number; sentiment: DiffSentiment }) {
  const color = value === 0 ? 'dimmed' : sentiment === 'positive' ? (value > 0 ? 'green' : 'red') : (value < 0 ? 'green' : 'red');
  const prefix = value > 0 ? '+' : '';
  return <Text size="sm" c={color} fw={500}>{prefix}{value}</Text>;
}

function fmt(n: number) {
  return <Text size="sm" ta="center">{n}</Text>;
}

export default function MatchStats() {
  const [teams, setTeams] = useState<string[]>([]);
  const [team, setTeam] = useState<string | null>(null);
  const [from, setFrom] = useState<Date>(defaultFrom());
  const [to, setTo] = useState<Date>(new Date());
  const [stats, setStats] = useState<MatchStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    new HttpClient<FilterOptions>('/match/filter-options').getSingle().then(opts => {
      if (opts?.teams?.length) {
        setTeams(opts.teams);
        setTeam(opts.teams[0]);
      }
    });
  }, []);

  useEffect(() => {
    if (!team || !from || !to) return;
    setLoading(true);
    const params = new URLSearchParams({ team, from: from.toISOString(), to: to.toISOString() });
    new HttpClient<MatchStatsResponse>(`/match/stats?${params}`).getSingle()
      .then(data => setStats(data))
      .finally(() => setLoading(false));
  }, [team, from, to]);

  const cur = stats?.current ?? empty;
  const prev = stats?.previous ?? empty;

  const prevFrom = new Date(from);
  prevFrom.setMonth(prevFrom.getMonth() - 6);
  const prevTo = new Date(to);
  prevTo.setMonth(prevTo.getMonth() - 6);

  const fmtDate = (d: Date) => d.toLocaleDateString('de-DE');

  // Build top-player rows: top 5 current + fill with prev-only players
  const allPlayerNames = [
    ...cur.topPlayers.map(p => p.name),
    ...prev.topPlayers.filter(p => !cur.topPlayers.some(c => c.name === p.name)).map(p => p.name),
  ].slice(0, 8);

  const playerRows = allPlayerNames.map(name => {
    const c = cur.topPlayers.find(p => p.name === name)?.minutes ?? 0;
    const p = prev.topPlayers.find(p => p.name === name)?.minutes ?? 0;
    return { name, cur: c, prev: p, diff: c - p };
  });

  const statRows: { label: string; cur: number; prev: number; sentiment: DiffSentiment }[] = [
    { label: 'Spiele', cur: cur.matches, prev: prev.matches, sentiment: 'neutral' },
    { label: 'Siege', cur: cur.wins, prev: prev.wins, sentiment: 'positive' },
    { label: 'Unentschieden', cur: cur.draws, prev: prev.draws, sentiment: 'neutral' },
    { label: 'Niederlagen', cur: cur.losses, prev: prev.losses, sentiment: 'negative' },
    { label: 'Punkte', cur: cur.points, prev: prev.points, sentiment: 'positive' },
    { label: 'Tore erzielt', cur: cur.goalsScored, prev: prev.goalsScored, sentiment: 'positive' },
    { label: 'Tore erhalten', cur: cur.goalsConceded, prev: prev.goalsConceded, sentiment: 'negative' },
  ];

  return (
    <Box maw={800} mx="auto" mt="lg" px="md">
      <Title order={3} mb="md">Mannschaftsstatistik</Title>

      <Group mb="lg" align="flex-end" gap="sm" wrap="wrap">
        <Select
          label="Mannschaft"
          data={teams}
          value={team}
          onChange={setTeam}
          w={200}
          clearable={false}
        />
        <DateInput
          label="Von"
          value={from}
          onChange={v => v && setFrom(new Date(v))}
          valueFormat="DD.MM.YYYY"
          w={140}
        />
        <DateInput
          label="Bis"
          value={to}
          onChange={v => v && setTo(new Date(v))}
          valueFormat="DD.MM.YYYY"
          w={140}
        />
        {loading && <Loader size="sm" mt={22} />}
      </Group>

      {team && (
        <Text size="xs" c="dimmed" mb="sm">
          Zeitraum: {fmtDate(from)} – {fmtDate(to)} &nbsp;|&nbsp; Vorjahr: {fmtDate(prevFrom)} – {fmtDate(prevTo)}
        </Text>
      )}

      <Stack gap="xl">
        <Table withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={160}>Kennzahl</Table.Th>
              <Table.Th ta="center">Aktuell</Table.Th>
              <Table.Th ta="center">Vorjahr</Table.Th>
              <Table.Th ta="center">Änderung</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {statRows.map(row => (
              <Table.Tr key={row.label}>
                <Table.Td fw={500}><Text size="sm">{row.label}</Text></Table.Td>
                <Table.Td ta="center">{fmt(row.cur)}</Table.Td>
                <Table.Td ta="center">{fmt(row.prev)}</Table.Td>
                <Table.Td ta="center"><DiffCell value={row.cur - row.prev} sentiment={row.sentiment} /></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {playerRows.length > 0 && (
          <>
            <Title order={5} mb={-8}>Top Spieler nach Spielminuten</Title>
            <Table withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Spieler</Table.Th>
                  <Table.Th ta="center">Minuten aktuell</Table.Th>
                  <Table.Th ta="center">Minuten Vorjahr</Table.Th>
                  <Table.Th ta="center">Änderung</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {playerRows.map(row => (
                  <Table.Tr key={row.name}>
                    <Table.Td><Text size="sm">{row.name}</Text></Table.Td>
                    <Table.Td ta="center">{fmt(row.cur)}</Table.Td>
                    <Table.Td ta="center">{fmt(row.prev)}</Table.Td>
                    <Table.Td ta="center"><DiffCell value={row.diff} sentiment="positive" /></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </>
        )}

        {!loading && stats && playerRows.length === 0 && cur.matches > 0 && (
          <Text size="sm" c="dimmed">Keine Spielerminuten-Daten für diesen Zeitraum.</Text>
        )}

        {!loading && stats && cur.matches === 0 && (
          <Text size="sm" c="dimmed" ta="center" mt="xl">Keine abgeschlossenen Spiele im gewählten Zeitraum.</Text>
        )}
      </Stack>
    </Box>
  );
}
